/* eslint-disable indent */
import styled from "styled-components";
import tw from "tailwind-styled-components";
import { PageLayout } from "../../components/Layout/PageLayout";
import { MobileAppDownloadBanner } from "../../components/MobileAppDownloadBanner/MobileAppDownloadBanner";
import { formatFrenchFullDate } from "../../services/blog";
import { markdownToHtml } from "../../services/markdown";
import { renderSection } from "../../services/section";
import { getStrapiMediaUrl } from "../../services/strapiApi";
import { capitalize } from "../../services/utils";
import {
  FooterSectionType,
  NavigationBarType,
  PageArticleType,
  PageSectionType,
  PreFootersType,
} from "../../types";
import { BlogBreadcrumbs } from "../components";
import { PreFooterTypesSection } from "../components/Sections/PreFooterTypesSection";
import { RealButton } from "../../components";
import { CTAType } from "../../types/strapi/CTAType";

interface PageArticleProps {
  navBarSection: PageSectionType<NavigationBarType>;
  footerSection: PageSectionType<FooterSectionType>;
  preFooterType?: PreFootersType;
  page: PageArticleType;
  overrideLogoUrl?: string;
}

export function PageArticle(props: PageArticleProps): JSX.Element {
  const { page, navBarSection, footerSection, preFooterType, overrideLogoUrl } =
    props;

  const category = page.categories.data[0];

  return (
    <PageLayout>
      {renderSection(
        navBarSection,
        undefined,
        true,
        undefined,
        undefined,
        overrideLogoUrl
      )}
      <MobileAppDownloadBanner />
      <BreadcrumbdsContainer>
        <BlogBreadcrumbs
          steps={[
            { title: "Blog", link: "/blog" },
            {
              title: category?.attributes.name ?? "...",
              link: category?.attributes.slug
                ? `/blog/${category.attributes.slug}`
                : undefined,
            },
            {
              title: page.title,
              link: `/blog/article/${page.slug}`,
            },
          ]}
        />
      </BreadcrumbdsContainer>
      <Article>
        <Title>{page.title}</Title>
        <InfoRow>
          <InfoText>
            Publié par <InfoTextEmphasis>{page.author}</InfoTextEmphasis> le{" "}
            <InfoTextEmphasis>
              {capitalize(formatFrenchFullDate(new Date(page.date)))}
            </InfoTextEmphasis>
          </InfoText>
        </InfoRow>
        <CoverImageContainer>
          <CoverImage
            src={getStrapiMediaUrl(page.coverImage.data.attributes)}
          />
        </CoverImageContainer>
        {page.cta && (
          <div className="lg:sticky lg:top-5 lg:pl-5">
            {page.cta.map((button: CTAType, i: number) => (
              <div className="lg:inline-block justify-center flex">
                <RealButton
                  key={`CardsSection-RealButton-${i}`}
                  onClick={() =>
                    window.open(
                      button?.destination,
                      button?.newWindow ? "_blank" : ""
                    )
                  }
                  color={button?.bgColor}
                  className="lg:mb-0 mb-8"
                >
                  {button?.label}
                </RealButton>
              </div>
            ))}
          </div>
        )}
        <Content
          dangerouslySetInnerHTML={{
            __html: markdownToHtml(page.content),
          }}
          className="prose break-words prose-img:h-96 prose-img:w-full prose-img:object-contain"
        />
      </Article>
      {preFooterType && <PreFooterTypesSection data={preFooterType} />}
      {renderSection(footerSection)}
    </PageLayout>
  );
}

const Article = tw.div`
  flex
  flex-col 
  w-full
  h-full
`;

const Title = tw.h1`
  w-full
  min-w-full
  px-8
  lg:px-36
  text-center
  text-lg
  font-bold
  mt-24
`;

const ContentCSS = styled.p`
  padding-left: 5vw;
  padding-right: 5vw;
  max-width: 90vw;

  @media (min-width: 768px) {
    padding-left: 25vw;
    padding-right: 25vw;
    max-width: 50vw;
  }
  overflow: hidden;

  a {
    color: #0000ee;
  }

  p > img {
    @media (min-width: 768px) {
      max-width: 50vw;
    }
    max-width: 90vw;
    margin-bottom: 0;
    margin-top: 40px;
    height: auto;
  }

  img + em {
    @media (min-width: 768px) {
      max-width: 50vw;
    }
    text-align: center;
    font-size: 0.8em;
    color: #9ca3af;
    max-width: 90vw;
    margin: 0 auto;
    margin-bottom: 40px;
  }

  p:has(> img) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin: 0 auto;
  }
`;

const Content = tw(ContentCSS)`
  w-full
  min-w-full
  pb-20
`;

const CoverImageContainer = tw.div`
  w-full 
  max-h-144
  overflow-hidden
  my-16
`;

const CoverImage = tw.img`
  object-cover
  bg-contain
  bg-center
  w-full
  h-full
`;

const InfoRow = tw.div`
  flex
  flex-row
  w-full
  justify-center
`;

const InfoText = tw.p`
  text-gray-500
  text-md
  mt-6
  px-2
  text-center
`;

const InfoTextEmphasis = tw.span`
  text-md
  text-black
`;

const BreadcrumbdsContainer = tw.div`
  w-full
  pl-8
  pt-8
  hidden
  lg:block
`;
